/**
 * Unauthenticated client for the public read-only ORDER view
 * (`/api/v1/public/orders/:token`). No bearer — this link is opened from a
 * Telegram alert or by scanning the QR on a printed order, by someone who is
 * very often not signed in and may not even work here.
 *
 * Deliberately narrower than the invoice DTO: an order link is BROADCAST into a
 * staff Telegram group and can be forwarded anywhere, so it carries no customer
 * name, phone or delivery address. See public-order.controller.ts for the
 * server's side of that decision.
 */
import axios from 'axios';

import { assetUrl } from './publicApi.js';

export interface PublicOrder {
  orderNumber: string;
  date: string;
  /** 'submitted' | 'fulfilled' — a cancelled order's link is never served. */
  status: string;
  currencyCode: string;
  /** Stable key ('cod' | 'bank_transfer' | 'pay_later' | 'fib') — localised client-side. */
  paymentMethod: string | null;
  /** True when a FIB payment for this order is confirmed server-side. */
  fibPaid?: boolean;
  seller: { name: string; logoUrl: string | null; tagline: string | null; email: string | null; phone: string | null; addressLines: string[] };
  lines: Array<{ description: string; quantity: string; unitPrice: string; lineTotal: string; isCharge: boolean }>;
  subtotal: string;
  discountTotal: string;
  taxTotal: string;
  total: string;
  couponCode: string | null;
  cashbackRedeemed: string;
  /** total − cashbackRedeemed: the wallet draw settles the order, it does not re-price it. */
  amountDue: string;
  /** Backend per-currency decimals (code → exponent) so IQD renders as whole dinars. */
  currencyDecimals: Record<string, number>;
}

const rawBase = import.meta.env.VITE_API_BASE_URL || '/api/v1';
const client = axios.create({ baseURL: rawBase, headers: { 'Content-Type': 'application/json' } });

export async function getPublicOrder(token: string): Promise<PublicOrder> {
  return (await client.get<PublicOrder>(`/public/orders/${encodeURIComponent(token)}`)).data;
}

export { assetUrl };
